import { registerProposalDraft, notifyDrafts } from '../../lib/proposalDraftRegistry';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input, Textarea } from '@mui/joy';
import type { InputProps, TextareaProps } from '@mui/joy';

/** How long typing must pause before the edit is sent to the server. */
export const SAVE_DEBOUNCE_MS = 500;

/**
 * Shared behaviour for fields that auto-save as you type.
 *
 * Binding an input straight to server data and saving on every keystroke makes
 * the field unusable: the mutation re-renders the component before any state
 * has changed, so React resets the DOM back to the old server value and the
 * character just typed disappears. Even once a save lands, the refetch it
 * triggered can arrive after later keystrokes and stomp on them.
 *
 * The fix is the one `useDebouncedRename` applies to renames:
 *  - edit a local draft, and only save once typing pauses (or the field blurs),
 *  - treat the draft as newer than anything the server sends until the server
 *    echoes back the value we saved.
 */
const isEcho = (serverValue: string, sent: string, numeric: boolean): boolean => {
  if (serverValue === sent) return true;
  // The server normalises what we send: DRF trims strings, and decimal fields
  // come back padded ("75" is stored and returned as "75.00"). Both still count
  // as our own edit coming home.
  if (serverValue.trim() === sent.trim()) return true;
  if (!numeric) return false;
  const a = Number(serverValue);
  const b = Number(sent);
  return Number.isFinite(a) && Number.isFinite(b) && a === b;
};

const useDebouncedDraft = (
  value: string | number | null | undefined,
  onCommit: (value: string) => void,
  delay: number,
  numeric: boolean,
) => {
  const serverValue = value == null ? '' : String(value);
  const serverRef = useRef(serverValue); serverRef.current = serverValue;

  const [draft, setDraft] = useState(serverValue);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The edit we last sent. While set, incoming server values are ignored — they
  // may predate this edit. Cleared once the server echoes the same value back.
  const sentRef = useRef<string | null>(null);
  // Read the callback and the draft through refs so a pending save never fires
  // with a stale closure.
  const commitRef = useRef(onCommit);
  const draftRef = useRef(draft);
  useEffect(() => {
    commitRef.current = onCommit;
    draftRef.current = draft;
  });

  const send = useCallback((next: string) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    sentRef.current = isEcho(serverRef.current, next, numeric) ? null : next;
    commitRef.current(next);
    notifyDrafts();
  }, []);

  const handleChange = useCallback(
    (next: string) => {
      draftRef.current = next;
      setDraft(next);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => send(next), delay);
      notifyDrafts();
    },
    [delay, send],
  );

  // Save immediately when the field is left, so clicking straight into Save (or
  // another tab) never drops the last few characters.
  const handleBlur = useCallback(() => {
    if (timerRef.current) send(draftRef.current);
  }, [send]);

  useEffect(() => {
    if (sentRef.current !== null) {
      // Waiting on our own edit to come back; anything else is stale.
      if (isEcho(serverValue, sentRef.current, numeric)) { sentRef.current = null; notifyDrafts(); }
      return;
    }
    setDraft((current) => (current === serverValue ? current : serverValue));
  }, [serverValue, numeric]);

  useEffect(() => registerProposalDraft({
    dirty: () => timerRef.current !== null || sentRef.current !== null,
    flush: () => {
      if (timerRef.current || sentRef.current !== null) send(draftRef.current);
    },
  }), [send]);

  // Flush on unmount (switching tabs, collapsing the row) so a pending edit is
  // still saved.
  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        commitRef.current(draftRef.current);
      }
    },
    [],
  );

  return { draft, handleChange, handleBlur };
};

type DebouncedFieldProps = {
  /** Current value from the server. */
  value: string | number | null | undefined;
  /** Called with the typed text once editing pauses or the field blurs. */
  onCommit: (value: string) => void;
  /** Pause before saving, in ms. */
  delay?: number;
};

export type DebouncedInputProps = Omit<InputProps, 'value' | 'onChange' | 'onBlur'> &
  DebouncedFieldProps;

/** Joy `Input` that keeps a local draft and saves on a debounce. */
export const DebouncedInput: React.FC<DebouncedInputProps> = ({
  value,
  onCommit,
  delay = SAVE_DEBOUNCE_MS,
  ...inputProps
}) => {
  const { draft, handleChange, handleBlur } = useDebouncedDraft(
    value,
    onCommit,
    delay,
    inputProps.type === 'number',
  );

  return (
    <Input
      {...inputProps}
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
    />
  );
};

export type DebouncedTextareaProps = Omit<TextareaProps, 'value' | 'onChange' | 'onBlur'> &
  DebouncedFieldProps;

/** Joy `Textarea` that keeps a local draft and saves on a debounce. */
export const DebouncedTextarea: React.FC<DebouncedTextareaProps> = ({
  value,
  onCommit,
  delay = SAVE_DEBOUNCE_MS,
  ...textareaProps
}) => {
  const { draft, handleChange, handleBlur } = useDebouncedDraft(value, onCommit, delay, false);

  return (
    <Textarea
      {...textareaProps}
      value={draft}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
    />
  );
};

export default DebouncedInput;
